import { useState, useRef, useEffect } from 'react'
import { X, Eraser, Loader2, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface SignatureModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (signatureDataUrl: string) => Promise<void>
  orderNumber: string
  customSignImageUrl?: string
}

export default function SignatureModal({
  isOpen,
  onClose,
  onSave,
  orderNumber,
  customSignImageUrl,
}: SignatureModalProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [hasSignature, setHasSignature] = useState(false)

  useEffect(() => {
    if (isOpen && canvasRef.current) {
      const canvas = canvasRef.current
      const ctx = canvas.getContext('2d')
      if (ctx) {
        // Set white background
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, canvas.width, canvas.height)
        // Set pen style
        ctx.strokeStyle = '#000000'
        ctx.lineWidth = 2
        ctx.lineCap = 'round'
        ctx.lineJoin = 'round'
      }
      setHasSignature(false)
    }
  }, [isOpen])

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) return
    
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    setIsDrawing(true)
    const rect = canvas.getBoundingClientRect()
    
    // Calculate scale factors to handle canvas display size vs actual size
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    
    let x: number, y: number
    if ('touches' in e) {
      x = (e.touches[0].clientX - rect.left) * scaleX
      y = (e.touches[0].clientY - rect.top) * scaleY
    } else {
      x = (e.clientX - rect.left) * scaleX
      y = (e.clientY - rect.top) * scaleY
    }
    
    ctx.beginPath()
    ctx.moveTo(x, y)
  }

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return
    
    const canvas = canvasRef.current
    if (!canvas) return
    
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const rect = canvas.getBoundingClientRect()
    
    // Calculate scale factors
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    
    let x: number, y: number
    if ('touches' in e) {
      x = (e.touches[0].clientX - rect.left) * scaleX
      y = (e.touches[0].clientY - rect.top) * scaleY
    } else {
      x = (e.clientX - rect.left) * scaleX
      y = (e.clientY - rect.top) * scaleY
    }
    
    ctx.lineTo(x, y)
    ctx.stroke()
    setHasSignature(true)
  }

  const stopDrawing = () => {
    setIsDrawing(false)
  }

  const clearSignature = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    setHasSignature(false)
  }

  const saveSignature = async () => {
    if (!canvasRef.current || !hasSignature) return

    setIsSaving(true)
    try {
      const dataUrl = canvasRef.current.toDataURL('image/png')
      await onSave(dataUrl)
      onClose()
    } catch (error) {
      console.error('Failed to save signature:', error)
    } finally {
      setIsSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal Content */}
      <div className="relative z-10 w-full max-w-lg mx-4 bg-white dark:bg-gray-800 rounded-xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
            簽名 - {orderNumber}
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose} className="cursor-pointer">
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Body - 簽名區域加大，減少邊界 */}
        <div className="p-2 bg-gray-50 dark:bg-gray-900">
          {/* 客製簽單底圖 */}
          {customSignImageUrl && (
            <div className="mb-3 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white">
              <p className="text-xs text-gray-500 dark:text-gray-400 px-2 pt-1">簽單文件：</p>
              <img
                src={customSignImageUrl}
                alt="客製簽單底圖"
                className="w-full object-contain max-h-64"
              />
            </div>
          )}

          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            {customSignImageUrl ? '請在下方簽名（將合併至上方文件底部）：' : '請在下方簽名：'}
          </p>

          <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden bg-white">
            <canvas
              ref={canvasRef}
              width={500}
              height={250}
              className="w-full touch-none cursor-crosshair"
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />
          </div>
        </div>

        {/* Footer - All buttons in one row */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-between gap-3">
          <Button
            variant="outline"
            onClick={clearSignature}
            className="cursor-pointer"
          >
            <Eraser className="w-4 h-4 mr-2" />
            清除
          </Button>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} className="cursor-pointer">
              取消
            </Button>
            <Button
              onClick={saveSignature}
              disabled={!hasSignature || isSaving}
              className="cursor-pointer"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                 儲存中...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  確認
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
